import { useState, useEffect } from 'react';
import { Dialog, DialogContent, CircularProgress, Alert, Button } from '@mui/material';
import { Provider } from 'react-redux';
import { store as mainStore } from '../../../../app/store';
import { useGetDetailMetadataQuery, useGetEntityQuery } from '../../../grid/api/gridApi';
import { useAppDispatch } from '../../../../app/store';
import { DetailForm } from '../../../detail-form';
import type { LookupDataItem } from '../../../grid';
import { loadRelationLookupData } from '../../../detail-form/lookupDataLoader';
import type { ProjectScopedAllowedIds } from '../../../detail-form/lookupDataLoader';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import { finalizeTaskImages } from '../api/taskMediaService';

type TaskDetailModalInnerProps = {
  mode: 'add' | 'edit';
  taskId?: number;
  detailRouteId: number;
  title?: string;
  initialValues?: Record<string, unknown>;
  projectScopedAllowedIds?: ProjectScopedAllowedIds;
  developersNodeKey?: string;
  onClose: () => void;
  onCreated?: () => void;
};

const TaskDetailModalInner = ({
  mode,
  taskId,
  detailRouteId,
  title,
  initialValues,
  projectScopedAllowedIds,
  developersNodeKey,
  onClose,
  onCreated,
}: TaskDetailModalInnerProps) => {
  const dispatch = useAppDispatch();
  const [createdTaskId, setCreatedTaskId] = useState<number | undefined>();
  const [finalizationError, setFinalizationError] = useState<string | null>(null);
  const [isRetryingFinalization, setIsRetryingFinalization] = useState(false);
  const [draftId] = useState(() => {
    const fallbackUuid = () =>
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const value = Math.floor(Math.random() * 16);
        return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
      });
    const guid =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : fallbackUuid();
    return `draft-${guid}`;
  });

  const effectiveMode = createdTaskId != null ? 'edit' : mode;
  const effectiveTaskId = createdTaskId ?? taskId;

  const {
    data: detailMetadata,
    isLoading: metadataLoading,
    error: metadataError,
  } = useGetDetailMetadataQuery({ detailFormId: detailRouteId }, { skip: !detailRouteId });

  const {
    data: entity,
    isLoading: entityLoading,
    error: entityError,
  } = useGetEntityQuery(
    { nodeKey: detailMetadata?.contentKey ?? '', id: String(effectiveTaskId) },
    { skip: effectiveMode !== 'edit' || !detailMetadata?.contentKey || !effectiveTaskId }
  );

  const [lookupMaps, setLookupMaps] = useState<Record<string, Record<string, string>>>({});
  const [lookupOptionMaps, setLookupOptionMaps] = useState<Record<string, Record<string, string>>>(
    {}
  );
  const [lookupDataArrays, setLookupDataArrays] = useState<Record<string, LookupDataItem[]>>({});
  const [lookupLoading, setLookupLoading] = useState(false);

  const rawId = initialValues?.Id ?? initialValues?.id;
  const uploadContextId =
    effectiveMode === 'edit'
      ? effectiveTaskId
      : typeof rawId === 'string' || typeof rawId === 'number'
        ? rawId
        : draftId;

  const resolvedEntity = effectiveMode === 'edit' ? entity : (initialValues ?? {});

  useEffect(() => {
    if (!detailMetadata?.fields || !detailMetadata?.contentKey) return;
    if (effectiveMode === 'edit' && !entity) return;

    const fetchLookups = async () => {
      setLookupLoading(true);
      try {
        const { maps, displayMaps, arrays } = await loadRelationLookupData({
          dispatch,
          fields: detailMetadata.fields,
          contentKey: detailMetadata.contentKey,
          mode: effectiveMode,
          parentId: effectiveMode === 'edit' ? effectiveTaskId : undefined,
          entity: entity as Record<string, unknown> | undefined,
          initialValues,
          projectScopedAllowedIds,
          logPrefix: 'TaskDetailModal',
        });

        if (cancelled) return;
        setLookupMaps(displayMaps);
        setLookupOptionMaps(maps);
        setLookupDataArrays(arrays);
      } finally {
        if (!cancelled) setLookupLoading(false);
      }
    };

    let cancelled = false;
    fetchLookups();
    return () => {
      cancelled = true;
    };
  }, [
    detailMetadata,
    entity,
    effectiveMode,
    dispatch,
    effectiveTaskId,
    initialValues,
    projectScopedAllowedIds,
    developersNodeKey,
  ]);

  const isLoading = metadataLoading || (effectiveMode === 'edit' && entityLoading) || lookupLoading;
  const isError =
    !detailRouteId || Boolean(metadataError) || Boolean(effectiveMode === 'edit' && entityError);
  const isReady =
    !isLoading &&
    !isError &&
    Boolean(detailMetadata) &&
    (effectiveMode === 'add' || Boolean(entity));

  const finalizeCreatedTask = async (entityId: string, values: Record<string, unknown>) => {
    const hasDraftImages = Object.values(values).some(
      (value) => typeof value === 'string' && value.includes(`/${draftId}/`)
    );
    if (!hasDraftImages) return true;

    try {
      await finalizeTaskImages({ taskId: Number(entityId), draftId });
      return true;
    } catch (error) {
      setCreatedTaskId(Number(entityId));
      setFinalizationError(
        extractErrorMessage(error, 'Task was created, but its images were not finalized.')
      );
      return false;
    }
  };

  const retryFinalization = async () => {
    if (!createdTaskId) return;
    setIsRetryingFinalization(true);
    try {
      await finalizeTaskImages({ taskId: createdTaskId, draftId });
      onClose();
      onCreated?.();
    } catch (error) {
      setFinalizationError(
        extractErrorMessage(error, 'Failed to finalize task images. Please try again.')
      );
    } finally {
      setIsRetryingFinalization(false);
    }
  };

  return (
    <>
      {isLoading && (
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </DialogContent>
      )}
      {!isLoading && isError && (
        <DialogContent>
          <Alert severity="error">Failed to load task details.</Alert>
        </DialogContent>
      )}
      {isReady && (
        <DialogContent
          sx={{
            p: 0,
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {finalizationError && (
            <Alert
              severity="warning"
              sx={{ m: 2, mb: 0 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void retryFinalization()}
                  disabled={isRetryingFinalization}
                >
                  Retry
                </Button>
              }
            >
              {finalizationError}
            </Alert>
          )}
          <DetailForm
            key={`${effectiveMode}-${effectiveTaskId ?? 'new'}`}
            title={title ?? (effectiveMode === 'add' ? 'New Task' : 'Task Details')}
            metadata={detailMetadata!}
            initialValues={(resolvedEntity as Record<string, unknown>) ?? {}}
            mode={effectiveMode}
            basePath="/"
            suppressPerformReload
            lookupMaps={lookupMaps}
            lookupOptionMaps={lookupOptionMaps}
            lookupDataArrays={lookupDataArrays}
            parentId={
              effectiveMode === 'edit' && effectiveTaskId != null
                ? String(effectiveTaskId)
                : undefined
            }
            detailFormKey={detailMetadata!.contentKey}
            pruneEmptyTabs={effectiveMode === 'add'}
            richTextOptions={{
              enableMediaUpload: true,
              uploadContextId,
            }}
            onAfterCreate={finalizeCreatedTask}
            onDismiss={onClose}
            onSuccess={() => {
              onClose();
              onCreated?.();
            }}
          />
        </DialogContent>
      )}
    </>
  );
};

type TaskDetailModalProps = {
  open: boolean;
  mode: 'add' | 'edit';
  taskId?: number;
  detailRouteId: number;
  title?: string;
  initialValues?: Record<string, unknown>;
  projectScopedAllowedIds?: ProjectScopedAllowedIds;
  developersNodeKey?: string;
  onClose: () => void;
  onCreated?: () => void;
};

export const TaskDetailModal = ({
  open,
  mode,
  taskId,
  detailRouteId,
  title,
  initialValues,
  projectScopedAllowedIds,
  developersNodeKey,
  onClose,
  onCreated,
}: TaskDetailModalProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            '--Paper-shadow': 'none',
            boxShadow: 'none',
            bgcolor: 'background.default',
          },
        },
      }}
    >
      {open && (
        <Provider store={mainStore}>
          <TaskDetailModalInner
            mode={mode}
            taskId={taskId}
            detailRouteId={detailRouteId}
            title={title}
            initialValues={initialValues}
            projectScopedAllowedIds={projectScopedAllowedIds}
            developersNodeKey={developersNodeKey}
            onClose={onClose}
            onCreated={onCreated}
          />
        </Provider>
      )}
    </Dialog>
  );
};
